import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  HStack,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  OrderedList,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VisuallyHiddenInput,
  VStack,
} from '@cardboard-ui/react';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { t } from '@lingui/macro';
import { Card, CardContent } from 'components/cards/Card';
import { SimpleCard } from 'components/cards/SimpleCard';
import { InputDialog } from 'components/dialogs/InputDialog';
import { ErrorMessage } from 'components/ErrorMessage';
import { createDirectionalIcon } from 'components/icons/createIcon';
import { SuspendWithSpinner } from 'components/SuspendWithSpinner';
import { OverlayProps } from 'components/TaskOverlay';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PayloadError } from 'relay-runtime';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useMutation, useSafeRelay } from 'utils/graphClient';
import { authenticatedHttpRequest } from 'utils/http';
import { useI18n } from 'utils/i18n';
import { notEmpty } from 'utils/NotEmptyFilter';
import { useFeatureFlag } from 'utils/sessionProvider';
import { generatePassword, postMessageWTC } from '../../utils';
import { ManageSignatureCertificateModal_createDigitalSigningCertificate_Mutation } from './__generated__/ManageSignatureCertificateModal_createDigitalSigningCertificate_Mutation.graphql';
import { ManageSignatureCertificateModal_sessionInfo_Query } from './__generated__/ManageSignatureCertificateModal_sessionInfo_Query.graphql';

const CIcon = createDirectionalIcon(light('file-certificate'));

declare const TSA_URL: string;
declare const TSA_CERT_URL: string | null;

const sessionInfoQuery = graphql`
  query ManageSignatureCertificateModal_sessionInfo_Query {
    sessionInfo {
      member {
        id
        name
        email
      }
      digitalSigningCertificates {
        edges {
          node {
            id
            name
            email
            pfx
            validFrom {
              utcDatetime
            }
            validUntil {
              utcDatetime
            }
          }
        }
      }
    }
  }
`;

const createDigitalSigningCertificateMutation = graphql`
  mutation ManageSignatureCertificateModal_createDigitalSigningCertificate_Mutation(
    $password: String!
    $singleUse: Boolean
  ) {
    createDigitalSigningCertificate(
      input: { password: $password, singleUse: $singleUse }
    ) {
      digitalSigningCertificateCreated {
        id
        name
        email
        pfx
        validFrom {
          utcDatetime
        }
        validUntil {
          utcDatetime
        }
      }
      sessionInfo {
        digitalSigningCertificates {
          edges {
            node {
              name
              email
              pfx
              validFrom {
                utcDatetime
              }
              validUntil {
                utcDatetime
              }
            }
          }
        }
      }
    }
  }
`;

interface ManageSignatureCertificateModalProps extends OverlayProps {
  currentFileUrl: string;
}

type CertificateType = {
  id?: string;
  name: string;
  email: string;
  pfx: string;
  validUntil?: {
    utcDatetime: ISO8601DateTimeString;
  };
};

const UnsuspendedManageSignatureCertificateModal: React.FC<
  ManageSignatureCertificateModalProps
> = ({ isOpen, onClose, currentFileUrl }) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { sessionInfo } =
    useLazyLoadQuery<ManageSignatureCertificateModal_sessionInfo_Query>(
      sessionInfoQuery,
      {},
    );

  const certificates =
    sessionInfo.digitalSigningCertificates?.edges
      .map(({ node }) => node)
      .filter(notEmpty) || [];

  const isUploadSignatureCertificateEnabled = useFeatureFlag(
    'FEATURE_UPLOAD_SIGNATURE_CERTIFICATE',
  );

  const isSignWithSingleUseCertificateEnabled = useFeatureFlag(
    'FEATURE_SIGN_WITH_SINGLE_USE_CERTIFICATE',
  );

  const [selectedCertificate, setSelectedCertificate] =
    useState<CertificateType>();
  const [password, setPassword] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { format } = useI18n();
  const toast = useToast();

  const {
    isOpen: isCreateSignatureDialogOpen,
    onClose: onCreateSignatureDialogClose,
    onOpen: onCreateSignatureDialogOpen,
  } = useDisclosure();

  const {
    isOpen: isVerifySignatureDialogOpen,
    onClose: onVerifySignatureDialogClose,
    onOpen: onVerifySignatureDialogOpen,
  } = useDisclosure();

  const {
    isOpen: isDigitallySignDialogOpen,
    onClose: onDigitallySignDialogClose,
    onOpen: onDigitallySignDialogOpen,
  } = useDisclosure();

  const [
    createDigitalSigningCertificate,
    isCreateDigitalSigningCertificateLoading,
  ] =
    useMutation<ManageSignatureCertificateModal_createDigitalSigningCertificate_Mutation>(
      createDigitalSigningCertificateMutation,
    );

  const hasDigitalSigningWithTimestampFeature = useFeatureFlag(
    'FEATURE_DIGITAL_SIGNING_WITH_TIMESTAMP',
  );

  const observeWebViewer = useCallback((event: any) => {
    if (event.data.type === 'crdbrd-ctow-certificateVerified') {
      if (event.data.success) {
        onDigitallySignDialogOpen();
      } else {
        toast({
          title: t`Error on verifying the certificate`,
          description: t`Provided password was incorrect, please try again.`,
          status: 'error',
          isClosable: true,
        });
      }
    }

    if (event.data.type === 'crdbrd-ctow-signingFailed') {
      if (event.data.error) {
        toast({
          title: t`Error on signing the document`,
          description: t`Oops, there was an error while signing the document, please try again.`,
          status: 'error',
          isClosable: true,
        });
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', observeWebViewer);
    return () => window.removeEventListener('message', observeWebViewer);
  }, [observeWebViewer]);

  const handleCreateDigitalSigningCertificateError = (error: PayloadError) => {
    toast({
      title: t`Error on creating the certificate`,
      description: error.message,
      status: 'error',
      isClosable: true,
    });
  };

  const handlePFXFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files ? event.target.files[0] : undefined;
    if (file) {
      convertToDataUrl(file)
        .then((cert) => {
          const pfx = cert.replace('data:', '').replace(/^.+,/, '');
          setSelectedCertificate({
            name: sessionInfo.member?.name || '',
            email: sessionInfo.member?.email || '',
            pfx: pfx,
          });
          onVerifySignatureDialogOpen();
        })
        .catch(() => {
          toast({
            title: t`Error on uploading the certificate`,
            status: 'error',
            isClosable: true,
          });
        });
    }
  };

  const handleCreateSignatureCertificate = (inputPassword: string) => {
    setPassword(inputPassword);
    createDigitalSigningCertificate({
      variables: {
        password: inputPassword,
      },
      payloadErrorBehaviour: 'HANDLED',
      onCompleted: (data, errors) => {
        if (errors?.length) {
          errors.forEach(handleCreateDigitalSigningCertificateError);
          return;
        }

        setSelectedCertificate(
          data.createDigitalSigningCertificate
            ?.digitalSigningCertificateCreated,
        );
        onDigitallySignDialogOpen();
      },
      onError: handleCreateDigitalSigningCertificateError,
    });
  };

  const handleVerifyCertificatePassword = (inputPassword: string) => {
    if (selectedCertificate && inputPassword) {
      setPassword(inputPassword);
      verifyCertificatePassword({
        cert: selectedCertificate.pfx,
        password: inputPassword,
      });
    }
  };

  const handleDigitallySigning = async (location: string) => {
    if (selectedCertificate && password) {
      const approvalCertData = {
        cert: selectedCertificate.pfx,
        password,
        location,
      };
      const currentFileUrlForSigning = await fetchCurrentFileUrl(
        currentFileUrl,
      );
      await processSigning(
        approvalCertData,
        currentFileUrlForSigning,
        hasDigitalSigningWithTimestampFeature,
      );
      onDigitallySignDialogClose();
    }
  };

  const handleDigitallySigningWithSingleUseCertificate = () => {
    const singleUsePassword = generatePassword();

    createDigitalSigningCertificate({
      variables: {
        password: singleUsePassword,
        singleUse: true,
      },
      payloadErrorBehaviour: 'HANDLED',
      onCompleted: (data, errors) => {
        if (errors?.length) {
          errors.forEach((error) => {
            handleCreateDigitalSigningCertificateError(error);
          });
        } else {
          const singleUseCertificate =
            data.createDigitalSigningCertificate
              ?.digitalSigningCertificateCreated;

          if (singleUseCertificate) {
            fetchCurrentFileUrl(currentFileUrl).then(
              (currentFileUrlForSigning) => {
                const approvalCertData = {
                  cert: singleUseCertificate.pfx,
                  password: singleUsePassword,
                };

                processSigning(
                  approvalCertData,
                  currentFileUrlForSigning,
                  hasDigitalSigningWithTimestampFeature,
                );
                onDigitallySignDialogClose();
              },
            );
          }
        }
      },
      onError: (error) => {
        handleCreateDigitalSigningCertificateError(error);
      },
    });
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent data-qa="manage-signature-certificate-modal-content">
          <ModalHeader>{t`Digitally signing the document`}</ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            <Stack spacing={6}>
              {isSignWithSingleUseCertificateEnabled ? (
                <Text fontSize="sm">{t`Add a digital certificate to sign the document or sign with a single use certificate.`}</Text>
              ) : (
                <Stack>
                  <Text fontSize="sm">{t`Add or create a digital certificate to sign the document. The certificate will be sent to others so they can verify your signature.`}</Text>
                  <Heading
                    size="sm"
                    pt={4}
                  >{t`Previous signature certificates.`}</Heading>
                  {certificates.length ? (
                    certificates.map((certificate) => (
                      <Card
                        key={certificate.id}
                        onClick={() => {
                          setSelectedCertificate(certificate);
                          onVerifySignatureDialogOpen();
                        }}
                      >
                        <CardContent>
                          <HStack spacing={2} alignItems="flex-start">
                            <Box pt={1}>
                              <CIcon size="2x" />
                            </Box>
                            <Stack spacing={0}>
                              <Text fontSize="sm">
                                {t`Certificate name: ${certificate.name}`}
                              </Text>
                              <Text fontSize="sm">
                                {t`Certificate email: ${certificate.email}`}
                              </Text>
                              <Text fontSize="xs" fontStyle="italic" pt={4}>
                                {t`Certificate id: ${certificate.id}`}
                              </Text>
                              <Text fontSize="xs" fontStyle="italic">
                                {t`Valid from: ${format(
                                  new Date(certificate.validFrom.utcDatetime),
                                  'PPPpppp',
                                )}`}
                              </Text>
                              <Text fontSize="xs" fontStyle="italic">
                                {t`Valid until: ${format(
                                  new Date(certificate.validUntil.utcDatetime),
                                  'PPPpppp',
                                )}`}
                              </Text>
                            </Stack>
                          </HStack>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Text
                      fontSize="sm"
                      fontStyle="italic"
                    >{t`No previous certificates available. Please upload or create a new one.`}</Text>
                  )}
                </Stack>
              )}

              <ButtonGroup
                pt={4}
                width="100%"
                colorScheme="blue"
                size="sm"
                justifyContent="flex-end"
              >
                {isUploadSignatureCertificateEnabled && (
                  <Button
                    onClick={() => {
                      fileInputRef?.current?.click();
                    }}
                  >{t`Upload certificate`}</Button>
                )}

                {isSignWithSingleUseCertificateEnabled ? (
                  <Button
                    onClick={handleDigitallySigningWithSingleUseCertificate}
                    isLoading={isCreateDigitalSigningCertificateLoading}
                  >{t`Sign with a single use certificate`}</Button>
                ) : (
                  <Button
                    onClick={onCreateSignatureDialogOpen}
                  >{t`Create new certificate`}</Button>
                )}
              </ButtonGroup>

              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                accept=".pfx"
                onChange={handlePFXFileInput}
              />
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <InputDialog
        title={t`Create a Password for Your Digital Signature Certificate`}
        submitButtonLabel={t`Create signature certificate`}
        isOpen={isCreateSignatureDialogOpen}
        onClose={onCreateSignatureDialogClose}
        onSubmitInput={handleCreateSignatureCertificate}
        isLoading={isCreateDigitalSigningCertificateLoading}
        placeholder={t`Password`}
        type="password"
        data-qa="create-certificate-password-dialog"
      >
        <VStack py={4} spacing={4}>
          <Text>{t`You're about to create a digital signature using a self-signed certificate. This certificate is legally binding but might not be recognized by all applications or systems.`}</Text>
          <Text>{t`The password you are creating has no link to your login password. You'll need this password every time you use the certificate to sign documents. Here are some tips for creating a secure password:`}</Text>
          <Box fontSize="xs" color="gray.500">
            <OrderedList>
              <ListItem>
                {t`A good password is a unique password. If you have a password manager, let it generate and save a password for you.`}
              </ListItem>
              <ListItem>
                {t`If creating a password manually, make it long and include a mix of lowercase and uppercase letters, numbers, and punctuation.`}
              </ListItem>
              <ListItem>
                {t`Don't reuse passwords from other accounts or create easily guessable passwords.`}
              </ListItem>
              <ListItem>
                {t`Please note that the email address used for this signature will be ${sessionInfo.member?.email}.`}
              </ListItem>
            </OrderedList>
            <Text>{t`You'll have the option to add a location to your digital certificate in the next step.`}</Text>
          </Box>
        </VStack>
      </InputDialog>

      <InputDialog
        title={t`Verify certificate password`}
        submitButtonLabel={t`Next`}
        isOpen={isVerifySignatureDialogOpen}
        onClose={onVerifySignatureDialogClose}
        onSubmitInput={handleVerifyCertificatePassword}
        placeholder={t`Password`}
        type="password"
      />

      <InputDialog
        title={t`Digitally sign the document`}
        submitButtonLabel={t`Sign`}
        isOpen={isDigitallySignDialogOpen}
        onClose={onDigitallySignDialogClose}
        onSubmitInput={handleDigitallySigning}
        isRequired={false}
        placeholder={t`Location (optional)`}
        data-qa="digitally-sign-the-document-dialog"
      >
        <Box py={4}>
          <Text>{t`Signature details.`}</Text>
          <Text color="gray.500">{t`Reason: Document signing`}</Text>
          <Text color="gray.500">{t`Contact Details: ${
            selectedCertificate && 'email' in selectedCertificate
              ? selectedCertificate.email
              : sessionInfo.member?.email
          }`}</Text>
        </Box>
      </InputDialog>
    </>
  );
};

const verifyCertificatePassword = (approvalCertData: ApprovalCertDataType) => {
  postMessageWTC({
    type: 'verifyCertificate',
    approvalCertData,
  });
};

const convertToDataUrl = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      const data =
        typeof fileReader.result === 'string' ? fileReader.result : '';
      resolve(data);
    };

    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

const fetchCurrentFileUrl = async (currentFileUrl: string) => {
  const response = await authenticatedHttpRequest(`${currentFileUrl}.json`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
    },
  }).then((result) => result.json());
  return response.url;
};

type ApprovalCertDataType = {
  cert: string;
  password: string;
  location?: string;
};
const processSigning = async (
  approvalCertData: ApprovalCertDataType,
  currentFileUrl: string,
  signWithTimestamp: boolean,
) => {
  postMessageWTC({
    type: 'processSigning',
    approvalCertData,
    currentFileUrl,
    signWithTimestamp,
    timeStampConfig: {
      tsaUrl: TSA_URL,
      tsaCertUrl: TSA_CERT_URL,
    },
  });
};

export function ManageSignatureCertificateModal(
  props: ManageSignatureCertificateModalProps,
) {
  return (
    <ErrorBoundary fallback={<ErrorFallback {...props} />}>
      <SuspendWithSpinner>
        <UnsuspendedManageSignatureCertificateModal {...props} />
      </SuspendWithSpinner>
    </ErrorBoundary>
  );
}

const ErrorFallback = ({
  isOpen,
  onClose,
}: ManageSignatureCertificateModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalBody>
          <SimpleCard width="100%">
            <ErrorMessage
              size="lg"
              title={t`Error loading digital certificates.`}
            />
          </SimpleCard>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
